import { DoubleSide, Euler, Frustum, BufferGeometry, Group, LinearFilter, Matrix4, Mesh, MeshBasicMaterial, PlaneGeometry, Raycaster, ShaderMaterial, Sphere, Texture, Vector2, Vector3, Float32BufferAttribute, } from "three";
import { base64logo, isDefined, isNull } from "../../internal";
import { CustomShader, PanoUtils, QuadtreeUtils } from "../../utils/internal";
import { ImageQuadtree, PanoTile } from "./internal";
/**
 * Panorama Quadtree Node
 *
 * @author Benjamin Loesch, Robert Wüest
 * @class PanoImageQuadtree
 * @param {SceneGraphNode} parentNode
 * @extends {SceneGraphNode}
 * @constructor
 */
export class PanoImageQuadtree extends ImageQuadtree {
    constructor(baseUrls, tileWidth = 612, imageWidth, imageHeight, pxsize = 0, minZoom = 0, maxZoom = 2, cameraCount, camInfo) {
        super(baseUrls, tileWidth, imageWidth, imageHeight, pxsize, minZoom, maxZoom, 20);
        /**
         * list all available tiles
         * @property {Object} tiles
         * @default {}
         */
        this.tiles = [];
        this.curZoom = 1;
        this._gamma = 1.0;
        this._sceneCameraSettings = [];
        /**
         * Ladybug camera model specific scaling factor
         * @property {number} ttSize
         */
        this.ttSize = 1.5;
        /**
         * Usually if width > height = width else height
         * @property{number} tileDataSize
         */
        this.tileDataSize = 0;
        this.camInfo = [];
        /**
         * The Sceneobject wich contains all panorama tiles
         * @property {Scene} _panoQuadtreeScene
         */
        this._panoQuadtreeScene = null;
        this._tilesLoaded = false;
        this.faceindexprev = -1;
        this._latlonprev = [0, 0];
        /**
         * @property {number} callback is fired when panocamera rotates
         * @default null
         */
        this.onRotate = null;
        /**
         * The lat and lon angle to turn camera into middle of the actuall callote
         * @type {Array}
         * @private
         */
        this._actualcalotteangles = [];
        this.requestedImageIndex = 0;
        /**
         * if smear is set to true, the callote borders are hidden by a smear effect running in shader
         */
        this.smear = 0;
        this.smearprev = 0;
        this.smearingEnabled = false;
        this._tMouseVector = new Vector3();
        this.cachingStepSize = 1;
        this.frameskippedcbf = null;
        this.camInfo = camInfo;
        this.tileDataSize = imageWidth > imageHeight ? imageWidth : imageHeight;
    }
    init(traversalState) {
        this._panoQuadtreeScene = traversalState.scene.tPanoQuadtreeScene;
        super.init(traversalState);
        this.createPanoramaTiles();
        this._imageChanged = true;
        const img = new Image();
        img.src = base64logo;
        //create infra3d logo
        const texture = new Texture(img);
        img.onload = function () {
            texture.needsUpdate = true;
            texture.minFilter = LinearFilter;
        };
        const material = new MeshBasicMaterial({
            transparent: true,
            map: texture,
            side: DoubleSide,
        });
        const plane = new Mesh(new PlaneGeometry(0.5, 0.5, 1, 1), material);
        plane.position.x = 0;
        plane.position.y = -1;
        plane.position.z = 0;
        plane.rotateX(-90);
        this._panoQuadtreeScene.add(plane);
    }
    destroy() {
        super.destroy();
        this.currentImageIndex = -1;
    }
    /**
     * reads:   traversalState.lockRendering
     *          traversalState.cameras
     *          traversalState.panoCamChanged
     *          traversalState.gamma
     *          traversalState.actualMousePos
     *
     *
     * writes:  traversalState.actualQuadtree
     *
     * @method render
     *
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        super.render(traversalState);
        if (this.hide || this.currentImageIndex == -1)
            return;
        this.currentTraversalState = traversalState;
        if (this._cacheNotReadyEventSent)
            traversalState.lockRendering = true;
        // --- Measure view quadtree is active
        if (this.active) {
            if (this._cacheNotReadyEventSent)
                traversalState.lockRendering = true;
            const camera = traversalState.cameras["panoCamera"].iCamera;
            //check if panocamera was rotated an call callback and determine the actual main-calotte
            if (traversalState.panoCamChanged) {
                const latlon = traversalState.cameras["panoCamera"].getLatLon();
                if ((latlon[0] != this._latlonprev[0] ||
                    latlon[1] != this._latlonprev[1] ||
                    latlon[2] != this._latlonprev[2]) &&
                    this.onRotate != null) {
                    this.onRotate(latlon[0], latlon[1], latlon[2]);
                    this._latlonprev = latlon;
                }
                this.updateActualCalotte(traversalState);
            }
            // Render
            if (this.baseUrls[this.currentImageIndex] != "" && this.baseUrls[this.currentImageIndex] != undefined) {
                // Calculate optimized frustum list:
                const fovY = camera.fov / (180.0 / Math.PI);
                const frustum = new Frustum();
                frustum.setFromProjectionMatrix(new Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
                const zoom = Math.min(Math.max(Math.round(((this._sceneCameraSettings[0].cameraFov / fovY) *
                    0.75 *
                    traversalState.scene.engine.getContextHeight()) /
                    this.tileWidth) - 1, 0), this.maxZoom);
                this.curZoom = this._forceLod0 ? 0 : zoom > 0 ? zoom : 0;
                this._allTilesReady = true;
                //if image has changed delete all tiles from scene
                if (this._imageChanged)
                    this.wipeTileTextures();
                for (let i = 0; i < this.camInfo.length; i++) {
                    for (let lstI = 0; lstI < this.lstFrustumTiles[i].length; lstI++) {
                        this._panoQuadtreeScene.remove(this.lstFrustumTiles[i][lstI].tMesh);
                    }
                }
                for (let i = 0; i < this.camInfo.length; i++) {
                    this.lstFrustumTiles[i] = [];
                    this.createFrustumList(0, frustum, i);
                }
                for (let i = 0; i < this.camInfo.length; i++) {
                    for (let ik = 1; ik <= this.curZoom; ik++) {
                        this.createFrustumList(ik, frustum, i);
                    }
                    for (let xi = 0; xi < this.lstFrustumTiles[i].length; xi++) {
                        if (this.lstFrustumTiles[i][xi].tileReady) {
                            this._panoQuadtreeScene.add(this.lstFrustumTiles[i][xi].tMesh);
                        }
                        if (!(this.lstFrustumTiles[i][xi].tileReady &&
                            this.lstFrustumTiles[i][xi].initialized &&
                            this.lstFrustumTiles[i][xi].tMesh.material.uniforms.uTexture != undefined)) {
                            this._allTilesReady = false;
                        }
                    }
                }
                //check if all visible tiles are ready
                if (!this._allTilesReady && this._allTilesReadyPrev) {
                    //first cycle in which some tiles are not ready
                    if (this.visibleTilesNotReady)
                        this.visibleTilesNotReady();
                }
                if (this._allTilesReady && !this._allTilesReadyPrev) {
                    //first cycle in which all tiles are ready loaded again
                    if (this.visibleTilesReady)
                        this.visibleTilesReady();
                }
                this._allTilesReadyPrev = this._allTilesReady;
                //if there is a change to play-mode->turn camera into actual callote and zoom a bit to hide ugly black areas
                if (this._forceLod0 && !this._forceLod0prev) {
                    //change camera to actual callote angle...
                    traversalState.cameras["panoCamera"].setLatLon(this._actualcalotteangles[0], this._actualcalotteangles[1], 60);
                }
                if (this._forceLod0 != this._forceLod0prev) {
                    this.requestedImageIndex = this.currentImageIndex;
                    if (!isNull(this.cacheNotReadyEvent) && this._cacheNotReadyEventSent) {
                        this.cacheReadyEvent();
                        this._cacheNotReadyEventSent = false;
                    }
                    if (this.smearingEnabled)
                        this.smear = this._forceLod0 ? 0 : 1;
                }
                this._forceLod0prev = this._forceLod0;
                //caching stuff when switching to next frame
                if (this._imageChanged) {
                    this._imageChanged = false;
                    if (this.framechangedcbf != null)
                        this.framechangedcbf(this.currentImageIndex);
                }
                if (!this._allTilesReady)
                    traversalState.lockRendering = true;
            }
            //gamma update
            this._gamma = traversalState.gamma;
            if (this.smear != this.smearprev) {
                for (let i = 0; i < this._panoQuadtreeScene.children.length; i++) {
                    const mesh = this._panoQuadtreeScene.children[i];
                    const material = mesh.material;
                    if (material instanceof ShaderMaterial) {
                        material.uniforms["fSmear"] = { value: this.smear };
                        material.needsUpdate = true;
                    }
                }
            }
            this.smearprev = this.smear;
        }
        //update tMouseVector for MouseToMap calculations
        this._tMouseVector.x =
            2 * (traversalState.actualMousePos[0] / traversalState.scene.engine.getContextWidth()) - 1;
        this._tMouseVector.y =
            1 - 2 * (traversalState.actualMousePos[1] / traversalState.scene.engine.getContextHeight());
        this._tMouseVector.z = -1.0;
    }
    /**
     * updates the value in _actualcalotte according to the passed traversalState
     * @param traversalState
     */
    updateActualCalotte(traversalState) {
        //Set actual camera
        //determine actual calottes:
        //todo: nescessary to speed up this?
        const latlon = traversalState.cameras["panoCamera"].getLatLon();
        const d = [];
        const calottangles = [
            [0, 0],
            [0, 72],
            [0, 2 * 72],
            [0, 3 * 72],
            [0, 4 * 72],
            [90, 180],
        ];
        d.push(PanoUtils.getDistanceOnSphere(latlon, calottangles[0], 12));
        d.push(PanoUtils.getDistanceOnSphere(latlon, calottangles[1], 12));
        d.push(PanoUtils.getDistanceOnSphere(latlon, calottangles[2], 12));
        d.push(PanoUtils.getDistanceOnSphere(latlon, calottangles[3], 12));
        d.push(PanoUtils.getDistanceOnSphere(latlon, calottangles[4], 12));
        d.push(PanoUtils.getDistanceOnSphere(latlon, calottangles[5], 12));
        const minindex = d.indexOf(Math.min(...d));
        if (minindex != -1) {
            this._actualcalotteangles = calottangles[minindex];
            this.faceindex = minindex;
            //change projection center a little bit according to currently viewed callote...
            if (this.faceindex != this.faceindexprev) {
                this.currentTraversalState.actualCamera.iCamera.position.set(this.camInfo[this.faceindex].position.dx, this.camInfo[this.faceindex].position.dy, this.camInfo[this.faceindex].position.dz);
            }
            this.faceindexprev = this.faceindex;
        }
    }
    createFrustumList(zoomLevel, frustum, cameraIdx) {
        if (this.curZoom == 0 && cameraIdx != this.faceindex && this._forceLod0)
            return;
        const maxZoomLevel = this.maxZoom;
        const camTiles = this.tiles[cameraIdx];
        for (let xx = 0; xx < Math.pow(2, zoomLevel); xx++) {
            for (let yy = 0; yy < Math.pow(2, zoomLevel); yy++) {
                const tileToAddQuadKey = QuadtreeUtils.TileCoordToQuadKey(xx, yy, zoomLevel);
                const tileToAddCoord = QuadtreeUtils.QuadKeyToTileCoord(tileToAddQuadKey);
                const tileToAdd = camTiles[tileToAddQuadKey];
                if (this.currentTraversalState.gamma != this._gamma)
                    tileToAdd.tMesh.material.uniforms["uGamma"] = {
                        value: this.currentTraversalState.gamma,
                    };
                // Check if bottom layer is visible, more accurate boundingsphere check
                const children = QuadtreeUtils.GetChildrenAt(tileToAddQuadKey, maxZoomLevel);
                for (let cI = 0; cI < children.length; cI++) {
                    const quadKey = children[cI];
                    const tile = camTiles[quadKey];
                    const camI = this.camInfo[tile.cameraIndex];
                    const geometry = camTiles[quadKey].tGeometry;
                    const objTransform = new Group();
                    objTransform.rotation.set((camI.position.domega * Math.PI) / 180, (camI.position.dphi * Math.PI) / 180, (camI.position.dkappa * Math.PI) / 180);
                    const pos = new Vector3(camI.position.dx, camI.position.dy, camI.position.dz);
                    objTransform.position.set(pos.x, pos.y, pos.z);
                    objTransform.scale.set(this.ttSize, this.ttSize, this.ttSize);
                    objTransform.updateMatrixWorld(true);
                    if (geometry.boundingSphere === null)
                        geometry.computeBoundingSphere();
                    const center = new Vector3().copy(geometry.boundingSphere.center);
                    const sphere = new Sphere(center, geometry.boundingSphere.radius);
                    sphere.applyMatrix4(objTransform.matrixWorld);
                    if (frustum.intersectsSphere(sphere) === true) {
                        if (!tileToAdd.initialized) {
                            const url = this.baseUrls[this.currentImageIndex] +
                                "/" +
                                tileToAdd.cameraIndex +
                                "/?" +
                                "lod=" +
                                tileToAddCoord["lod"] +
                                "&x=" +
                                tileToAddCoord["x"] +
                                "&y=" +
                                tileToAddCoord["y"];
                            tileToAdd.loadTexture(url);
                        }
                        this.lstFrustumTiles[cameraIdx].push(tileToAdd);
                        break;
                    }
                }
            }
        }
    }
    /**
     * create all panotiles in an initial step
     */
    createPanoramaTiles() {
        this.wipeTiles();
        for (let i = 0; i < this.camInfo.length; i++) {
            this.tiles.push({});
            this.lstFrustumTiles.push([]);
            const cam = this.camInfo[i];
            const sensorDiagonal = Math.sqrt(Math.pow(2 * this.pixelsize * (2448 / 2), 2) + Math.pow(2 * this.pixelsize * (2048 / 2), 2));
            const camFov = 2 * Math.atan(sensorDiagonal / (2 * cam.focallength * 0.001));
            const camYaw = (cam.position.dphi < 0.0 ? 360 + cam.position.dphi : cam.position.dphi) / (180.0 / Math.PI);
            const camPitch = (cam.position.domega < 0.0 ? 360 + cam.position.domega : cam.position.domega) / (180.0 / Math.PI);
            const camRoll = (cam.position.dkappa < 0.0 ? 360 + cam.position.dkappa : cam.position.dkappa) / (180.0 / Math.PI);
            const camFP = new Vector2(cam.position.dx, cam.position.dz);
            const settings = {
                cameraFov: camFov,
                cameraYaw: camYaw,
                cameraPitch: camPitch,
                cameraRoll: camRoll,
                cameraFP: camFP,
                viewPlane: {
                    x0: (camYaw + camFov / 2.0) % (2 * Math.PI) < 0.0
                        ? 2 * Math.PI + ((camYaw + camFov / 2.0) % (2 * Math.PI))
                        : (camYaw + camFov / 2.0) % (2 * Math.PI),
                    x1: (camYaw - camFov / 2.0) % (2 * Math.PI) < 0.0
                        ? 2 * Math.PI + ((camYaw - camFov / 2.0) % (2 * Math.PI))
                        : (camYaw - camFov / 2.0) % (2 * Math.PI),
                    y0: (Math.PI / 2 - camFov / 2.0) % (2 * Math.PI) < 0.0
                        ? 2 * Math.PI + ((Math.PI / 2 - camFov / 2.0) % (2 * Math.PI))
                        : (Math.PI / 2 - camFov / 2.0) % (2 * Math.PI), //Omega & Kappa not included yet
                    y1: (Math.PI / 2 + camFov / 2.0) % (2 * Math.PI) < 0.0
                        ? 2 * Math.PI + ((Math.PI / 2 + camFov / 2.0) % (2 * Math.PI))
                        : (Math.PI / 2 + camFov / 2.0) % (2 * Math.PI), //Omega & Kappa not included yet
                },
            };
            this._sceneCameraSettings.push(settings);
            // create camera geometries
            for (let lod = 0; lod < this.maxZoom + 1; lod++) {
                if (lod > 0) {
                    for (let xx = 0; xx < Math.pow(2, lod); xx++) {
                        for (let yy = 0; yy < Math.pow(2, lod); yy++) {
                            const quadKey = QuadtreeUtils.TileCoordToQuadKey(xx, yy, lod);
                            this.tiles[i][quadKey] = this._generateTile(quadKey, i);
                        }
                    }
                }
                else {
                    this.tiles[i][""] = this._generateTile("", i);
                }
            }
        }
        this._tilesLoaded = true;
    }
    /**
     * clean up all textures e.g. when frame changes
     */
    wipeTileTextures() {
        for (let i = 0; i < this.camInfo.length; i++) {
            for (const key in this.tiles[i]) {
                this.tiles[i][key].initialized = false;
                this.tiles[i][key].tileReady = false;
                this._panoQuadtreeScene.remove(this.tiles[i][key].tMesh);
                this.tiles[i][key].destroy();
            }
        }
    }
    /**
     * called to destroy everything to release memory
     * @method wipeTiles
     *
     */
    wipeTiles() {
        if (this._tilesLoaded) {
            for (let i = 0; i < this.camInfo.length; i++) {
                for (const key in this.tiles[i]) {
                    this.tiles[i][key].initialized = false;
                    this.tiles[i][key].tileReady = false;
                    this._panoQuadtreeScene.remove(this.tiles[i][key].tMesh);
                    this.tiles[i][key].destroy();
                }
            }
            this._tilesLoaded = false;
        }
        this.tiles = [];
        this._sceneCameraSettings = [];
        super.wipeTiles();
        this.lstFrustumTiles = [];
    }
    /**
     * Read the pixel color value in RGBA [0.0 .. 1.0]
     * @method readPixelValue
     * @param {number} x
     * @param {number} y
     * @param {boolean} absolute
     * @return {number[]}
     */
    readPixelValue(x, y, absolute) {
        return [0.0, 0.0, 0.0];
    }
    /**
     * Wipe all active tiles and set the current quadtree image set
     * @method setImageIndex
     * @param {number} index
     *
     */
    setImageIndex(index, playmode = false, cachingstepsize) {
        if (this.currentImageIndex == -1)
            this._cacheNotReadyEventSent = true; //first call throw cachereadyevent
        this.determineDisplayMode(index, playmode);
        this.requestedImageIndex = index;
        this.cachingStepSize = cachingstepsize || 1;
        this._cacheSequence();
        if (this._cachingCompleted) {
            this.changeToRequestedImage();
        }
        else {
            if (!isNull(this.cacheNotReadyEvent) && !this._cacheNotReadyEventSent) {
                this.cacheNotReadyEvent();
                this._cacheNotReadyEventSent = true;
            }
        }
    }
    setActive(active) {
        this.active = active;
        if (isDefined(this.iMesh) && this.iMesh != null)
            this.iMesh.visible = active;
    }
    /**
     * @method _createTileGeometry
     * @param {number} quadCode f:number, imgwidth:number, imgheight:number, tileDataSize:number
     * @param {number} f
     * @param {number} imgwidth
     * @param {number} imgheight
     * @param {number} tileDataSize
     * @param {number} pixelSize
     * @return {Geometry}
     */
    _createTileGeometry(quadCode, f, imgwidth, imgheight, tileDataSize, pixelSize) {
        pixelSize = pixelSize * 1000;
        const tileC = QuadtreeUtils.QuadKeyToTileCoord(quadCode);
        const geom = new BufferGeometry();
        const tileSubdivision = 32 / Math.pow(2, tileC["lod"]);
        const tileSize = (tileDataSize * pixelSize) / Math.pow(2, tileC["lod"]);
        const step = (tileDataSize * pixelSize) / 32;
        let xs = (-imgwidth * pixelSize) / 2 + tileC.x * tileSize;
        const vertices = [];
        const uvs = [];
        const indices = [];
        // Generate vertices
        const verticesPerRow = tileSubdivision + 1;
        for (let i = 0; i < verticesPerRow; i++) {
            let ys = (-imgheight * pixelSize) / 2 + (Math.pow(2, tileC["lod"]) - tileC.y - 1) * tileSize;
            for (let j = 0; j < verticesPerRow; j++) {
                const x = xs;
                const y = ys;
                const r = Math.sqrt(x * x + y * y);
                const theta = r / f;
                const X = (-x / r) * Math.sin(theta);
                const Y = (-y / r) * Math.sin(theta);
                const Z = Math.cos(theta);
                const xlk = (X / Z) * -f;
                const ylk = (Y / Z) * -f;
                const zlk = -f;
                // Distance between original point on plane (x,y,-f) and re-projected point
                const d = new Vector3(xlk - x, ylk - y, zlk - -f).length();
                const alpha = Math.atan2(-f, r + d);
                let ppZ = 0;
                if (Math.abs(theta) < 0.1) {
                    ppZ = -f;
                }
                else {
                    ppZ = -f - d * Math.tan(alpha);
                }
                const ppX = x;
                const ppY = y;
                ys += step;
                vertices.push(ppX, ppY, ppZ);
                // Calculate UVs for this vertex
                const u = i / tileSubdivision;
                const v = j / tileSubdivision;
                uvs.push(u, v);
            }
            xs += step;
        }
        // Generate indices
        for (let i = 0; i < tileSubdivision; i++) {
            for (let j = 0; j < tileSubdivision; j++) {
                const a = i * verticesPerRow + j;
                const b = i * verticesPerRow + (j + 1);
                const c = (i + 1) * verticesPerRow + j;
                const d = (i + 1) * verticesPerRow + (j + 1);
                indices.push(a, c, b); // Face 1
                indices.push(c, d, b); //Face 2
            }
        }
        geom.setIndex(indices);
        geom.setAttribute("position", new Float32BufferAttribute(vertices, 3));
        geom.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
        geom.computeBoundingSphere();
        geom.computeBoundingBox();
        return geom;
    }
    _generateTile(quadCode, camIndex) {
        const camI = this.camInfo[camIndex];
        const extent = QuadtreeUtils.QuadKeyToNormalizedCoord(quadCode);
        const geometry = this._createTileGeometry(quadCode, camI.focallength, this.imageWidth, this.imageHeight, this.tileDataSize, this.pixelsize);
        const material = new ShaderMaterial({
            transparent: true,
            side: DoubleSide,
            wireframe: false,
            opacity: 1.0,
            uniforms: {
                uGamma: { value: 1.0 },
                uTexture: { value: null },
                fCalloteId: { value: camIndex },
                fLod: { value: quadCode.length },
                fSmear: { value: this.smear },
            },
            vertexShader: CustomShader.panoTileVertexShader,
            fragmentShader: CustomShader.panoTileFragmentShader,
        });
        const tMesh = new Mesh(geometry, material);
        tMesh.rotation.set((camI.position.domega * Math.PI) / 180, (camI.position.dphi * Math.PI) / 180, (camI.position.dkappa * Math.PI) / 180);
        const pos = new Vector3(camI.position.dx, camI.position.dy, camI.position.dz);
        tMesh.position.set(pos.x, pos.y, pos.z);
        tMesh.renderOrder = quadCode.length;
        tMesh.scale.set(this.ttSize, this.ttSize, this.ttSize);
        return new PanoTile(tMesh, geometry, camIndex, quadCode, extent, false, this);
    }
    /**
     * @method _checkAllTilesReady to handle tile ready events...
     * @param camId
     * @param quadCode
     * @returns {boolean}
     * @private
     */
    _checkAllTilesReady(camId, quadCode) {
        if (quadCode != "") {
            for (let i = 0; i < 4; i++) {
                const qC = quadCode.slice(0, -1) + i;
                if (!this.tiles[camId][qC].tileReady)
                    return false;
            }
        }
        return true;
    }
    _getParentTile(camI, quadCode) {
        if (quadCode != "") {
            const parent = this.tiles[camI][quadCode.slice(0, -1)];
            if (parent.quadCode != "" && !parent.tileReady) {
                return this._getParentTile(camI, parent.quadCode);
            }
            else {
                return parent;
            }
        }
        return null;
    }
    changeToRequestedImage() {
        if (this._cacheNotReadyEventSent) {
            this.cacheReadyEvent();
            this._cacheNotReadyEventSent = false;
        }
        if (this.currentImageIndex != this.requestedImageIndex) {
            this.currentImageIndex = this.requestedImageIndex;
            this._imageChanged = true;
            this.currentTraversalState.scene.render();
        }
    }
    /**
     * Get actual map coordinates: [0...1] https://github.com/iNovitas/infraVIS_SDK/wiki/Coordinate-Systems#map-coordinates
     * and actual callote Id.
     * @returns {number[]}
     */
    mouseToMap(mx, my, all) {
        const x = 2 * (mx / this.currentTraversalState.scene.engine.getContextWidth()) - 1;
        const y = 1 - 2 * (my / this.currentTraversalState.scene.engine.getContextHeight());
        // set two vectors with opposing z values
        const z = -1.0;
        let vector = new Vector3(x, y, z);
        const camera = this.currentTraversalState.cameras["panoCamera"].iCamera;
        let end = new Vector3(vector.x, vector.y, 1.0);
        vector = vector.unproject(camera);
        end = end.unproject(camera);
        // find direction from vector to end
        end.sub(vector).normalize();
        const rayCaster = new Raycaster(vector, end);
        const objects = rayCaster.intersectObjects(this._panoQuadtreeScene.children);
        const limit = all ? objects.length : 1;
        const result = new Array(5);
        for (let i = 0; i < limit; i++) {
            //because the pano-cam is at position 0,0,0 we need to do another ray-picking with rays origin a calotte's origin.
            const origin = objects[i].object.position.clone();
            const dir = objects[i].point.clone().sub(origin).normalize();
            const rayCaster = new Raycaster(origin, dir);
            const accurate_object = rayCaster.intersectObjects(this._panoQuadtreeScene.children);
            //get the first picked callote id
            const cid = objects[i].object.material.uniforms.fCalloteId.value;
            const u = accurate_object[0].point;
            const m = new Matrix4();
            u.applyMatrix4(m.copy(objects[i].object.matrix).invert());
            const v = new Vector3(0, 0, -1);
            const theta = Math.acos(u.dot(v) / (u.length() * v.length()));
            const f = this.camInfo[cid].focallength;
            const r = theta * f;
            const beta = Math.atan2(u.x, u.y);
            const pp = new Vector3(r * Math.sin(beta), r * Math.cos(beta), -f);
            const pix_coord_x = pp.x / (this.pixelsize * 1000) + this.imageWidth / 2;
            const pix_coord_y = -(pp.y / (this.pixelsize * 1000)) + this.imageHeight / 2;
            const nx = pix_coord_x / this.imageHeight; //this should be full zoom quadtree width
            const ny = pix_coord_y / this.imageHeight;
            const distance = objects[i].distance;
            //check if the hitted object is on deepest LOD
            const lod = accurate_object[0].object.userData.quadCode.length;
            if (isDefined(result[cid])) {
                if (lod > result[cid][3])
                    result[cid] = [nx, ny, cid, lod, distance];
            }
            else {
                result[cid] = [nx, ny, cid, lod, distance];
            }
        }
        for (let i = result.length; i > -1; i--) {
            if (!isDefined(result[i]))
                result.splice(i, 1);
        }
        return result;
    }
    mapToMouse(nx, ny, colid = 0) {
        const pos3d = this.mapTo3D(nx, ny, colid);
        pos3d.project(this.currentTraversalState.actualCamera.iCamera);
        const w = this.currentTraversalState.scene.engine.getContextWidth() / 2;
        const h = this.currentTraversalState.scene.engine.getContextHeight() / 2;
        pos3d.x = pos3d.x * w + w;
        pos3d.y = -(pos3d.y * h) + h;
        return [pos3d.x, pos3d.y];
    }
    /**
     * Get actual map coordinates: [0...1] https://github.com/iNovitas/infraVIS_SDK/wiki/Coordinate-Systems#map-coordinates
     * and actual callote Id.
     * @returns {number[]}
     */
    mapTo3D(nx, ny, calloteId) {
        const xoffset = (this.imageHeight - this.imageWidth) / this.imageHeight / 2;
        const x = (nx + xoffset - 0.5) * this.imageHeight * this.pixelsize * 1000;
        const y = -(ny - 0.5) * this.imageHeight * this.pixelsize * 1000;
        const f = this.camInfo[calloteId].focallength;
        const r = Math.sqrt(x * x + y * y);
        const theta = r / f;
        const z = -f;
        const X = (-x / r) * Math.sin(theta);
        const Y = (-y / r) * Math.sin(theta);
        const Z = Math.cos(theta);
        const xlk = (X / Z) * -f;
        const ylk = (Y / Z) * -f;
        const zlk = -f;
        const d = new Vector3(xlk - x, ylk - y, zlk - z).length();
        const alpha = Math.atan2(-f, r + d);
        let ppZ = 0;
        if (Math.abs(theta) < 0.1) {
            ppZ = -f;
        }
        else {
            ppZ = -f - d * Math.tan(alpha);
        }
        const ppX = x;
        const ppY = y;
        const v = new Vector3(ppX, ppY, ppZ);
        const camI = this.camInfo[calloteId];
        const mat = new Matrix4().makeRotationFromEuler(new Euler((camI.position.domega * Math.PI) / 180, (camI.position.dphi * Math.PI) / 180, (camI.position.dkappa * Math.PI) / 180));
        const pos = new Vector3(camI.position.dx, camI.position.dy, camI.position.dz);
        mat.setPosition(pos);
        mat.scale(new Vector3(this.ttSize, this.ttSize, this.ttSize));
        v.applyMatrix4(mat);
        return v;
    }
    smearOn() {
        this.smear = 1;
        this.smearingEnabled = true;
    }
    smearOff() {
        this.smear = 0;
        this.smearingEnabled = false;
    }
    setCacheRange(cacherange) {
        this._cachingRange = cacherange;
    }
}
//# sourceMappingURL=panoimagequadtree.js.map